/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Box } from '@achieve/ascend'
import { axisBottom, axisLeft, axisRight, max, scaleLinear, scaleBand, select } from 'd3'
import { DataSectionTest, numberFormatCurrency } from 'constants/DebtAssessment'

export const DatGraphBars = ({
  graphHeight = 350,
  data = DataSectionTest,
  textLegendAxisY = 'Monthly',
  subtextLegendAxisY = 'Payment',
  achieveLogo = 'https://images.ctfassets.net/80jeq2aeq4l1/1h9tWFoBq4p7v3Y9c9HzXs/11bae295d59e2128f9143e4d95b61441/a_logo.svg?w=3840',
  tooltipTitleAchieve = 'achieve',
  tooltipTitleCurrent = 'Current',
  tooltipTitleWait = 'If you wait',
  productType = 'AR',
  productTypeTerms = 0,
  colorGraph = '',
  showAchieveTooltip = true,
  showCurrentTooltip = true,
  showWaitTooltip = true,
  selectedValue,
}) => {
  const refContainer = useRef(null)
  const refChartSvg = useRef(null)
  const tooltipRefAchieve = useRef(null)
  const tooltipRefCurrent = useRef(null)
  const tooltipRefWait = useRef(null)
  const [graphWidth, setGraphWidth] = useState(0)
  const [svgChart, setSvgChart] = useState(null)
  const MARGIN = { top: 20, right: 30, bottom: 0, left: 60 }

  /**
   * Add tooltip markers to the graph
   * @param {string} title - title of marker
   * @param {string} value - value (monthly payment) of marker
   * @param {number} xAdd - number to adjust the position on the X axis (px)
   * @param {number} yAdd - number to adjust the position on the Y axis (px)
   * @param {any} tooltipRef - element reference where the marker will be rendered
   * @param {string} lineClassname - identifier of the class of the line, where the marker goes
   * @param {string} position - position of marker 'left' | 'middle' | 'right'
   */
  const addTooltip = function ({
    title,
    titleIcon,
    value,
    xAdd,
    yAdd,
    tooltipRef,
    lineClassname,
    showTooltip,
    titleStyle,
  }) {
    const mobileSpacing = graphWidth < 713 ? 30 : 0
    const pathElement = document.getElementsByClassName(lineClassname)[0]
    const x = Math.round(pathElement?.getAttribute('x')) + Math.round(xAdd + mobileSpacing)
    const y = Math.round(pathElement?.getAttribute('y')) + Math.round(yAdd)

    const tooltipDiv = tooltipRef.current
    if (tooltipDiv && showTooltip) {
      select(tooltipDiv).transition().duration(200).style('opacity', 1)
      select(tooltipDiv)
        .html(
          `
          <div class="text-[13px] rounded-t-[5px] p-[5px] flex gap-[5px] items-center justify-center ${titleStyle}" >
          ${
            titleIcon ? `<img alt="Achieve logo" width="20" height="16" src='${achieveLogo}'/>` : ''
          }
          ${title}
          </div>
          <div class='value'>${value}</div>
          `
        )
        .style('left', x + 'px')
        .style('top', y + 'px')
    }
  }

  /**
   * Add grid/reference lines for the graph
   * @param {number} width - grid width
   * @param {number} height - grid height
   * @param {any} svg - element reference of chart
   */
  const addGridLines = (width, height, svg) => {
    const y = scaleLinear().range([height, 0])
    const yAxisGrid = axisRight(y).tickSize(width).tickFormat('').ticks(7)
    const axisG = svg.append('g').call(yAxisGrid)
    axisG
      .selectAll('line')
      .attr('stroke', '#E7ECF3')
      .attr('stroke', (d, i) => (i == 0 ? 'none' : '#E7ECF3'))
    axisG.selectAll('path').attr('stroke', 'none')
  }

  const getGraphData = () => {
    if (data && data.achieveOffer && data.currentOffer && data.waitOffer) {
      return {
        achieveOffer: { ...data.achieveOffer },
        currentOffer: { ...data.currentOffer },
        waitOffer: { ...data.waitOffer },
      }
    }

    let achieveOffer = {
        totalCost: 0,
        monthlyPayment: 0,
        years: 0,
      },
      idxOffer,
      offersAvailable

    switch (productType) {
      case 'AR':
        idxOffer = 1
        offersAvailable = data?.default_offers?.ar_offers
        break
      case 'PL':
        idxOffer = 1
        offersAvailable = data?.default_offers?.apl_offers
        break
      case 'HL':
        idxOffer = 0
        offersAvailable = data?.default_offers?.ahl_offers
        break
    }

    if (productTypeTerms) {
      const idxFound = offersAvailable?.map((i) => i?.terms).indexOf(productTypeTerms) || -1
      if (idxFound >= 0) idxOffer = idxFound
    }
    offersAvailable = offersAvailable?.[idxOffer]

    achieveOffer = {
      totalCost: offersAvailable?.total_cost || 0,
      monthlyPayment: offersAvailable?.monthly_payment || 0,
      years: Math.round((offersAvailable?.terms || 0) / 12),
    }

    const currentOffer = {
      totalCost: Math.round(data?.current_assessment?.cfos?.payoff_plans?.[0]?.total_amount || 0),
      monthlyPayment: Math.round(
        data?.current_assessment?.cfos?.payoff_plans?.[0]?.starting_total_monthly_payment || 0
      ),
      years: Math.round(
        (data?.current_assessment?.cfos?.payoff_plans?.[0]?.time_in_months || 0) / 12
      ),
    }
    const waitOffer = {
      totalCost: Math.round(data?.projected_assessment?.cfos?.payoff_plans?.[0]?.total_amount || 0),
      monthlyPayment: Math.round(
        data?.projected_assessment?.cfos?.payoff_plans?.[0]?.starting_total_monthly_payment || 0
      ),
      years: Math.round(
        (data?.projected_assessment?.cfos?.payoff_plans?.[0]?.time_in_months || 0) / 12
      ),
    }

    return { achieveOffer, currentOffer, waitOffer }
  }

  /**
   * Generate graph function
   */
  const createGraph = async () => {
    const { achieveOffer, currentOffer, waitOffer } = getGraphData()

    // set the dimensions and margins of the graph
    const margin = MARGIN,
      width = graphWidth - margin.left - margin.right,
      height = graphHeight - margin.top - margin.bottom
    // append the svg object to the body of the page
    if (svgChart) svgChart.remove()
    const svg = select(refChartSvg.current)
      .append('svg')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top - 4})`)

    // Add X axis and Y axis
    const x = scaleLinear().range([0, width])
    const y = scaleLinear().range([height, 0])
    x.domain([0, 1500])
    y.domain([0, 1500])

    setSvgChart(svg)

    if (colorGraph)
      svg.append('rect').attr('width', width).attr('height', height).attr('fill', colorGraph)

    const axisX = svg
      .append('g')
      .attr('transform', `translate(0, ${height})`)
      .call(
        axisBottom(x)
          .ticks(7)
          .tickFormat(() => {
            return ``
          })
      )
    axisX.selectAll('line').attr('stroke', 'none')
    axisX.selectAll('path').attr('stroke', '#C0CBD8').attr('stroke-width', 2)

    const step = Math.ceil(selectedValue / 100)

    const axisY = svg.append('g').call(
      axisLeft(y)
        .ticks(7)
        .tickFormat((d, i) => {
          if (i === 4) return `$ ${i * step + step}+`
          if (i === 5) return subtextLegendAxisY
          if (i === 6) return textLegendAxisY
          if (i === 0) return 0

          return `$ ${i * step + step}`
        })

        .tickValues([0, 300, 600, 900, 1200, 1400, 1500])
    )

    axisY.selectAll('line').attr('stroke', 'none')
    axisY.selectAll('path').attr('stroke', '#C0CBD8').attr('stroke-width', 2)
    axisY
      .selectAll('text')
      .attr('color', '#616A7C')
      .attr('font-weight', '400')
      .attr('font-size', '12px')
      .attr('font-family', 'Ultramarine')

    addGridLines(width, 300, svg)

    axisY.selectAll('path').attr('d', function () {
      const d = select(this).attr('d')
      return d.replace('.5H-6', '').replace('M-6', 'M-0').replace('H-6', 'H-0')
    })

    axisX.selectAll('path').attr('d', function () {
      const d = select(this).attr('d')
      return d.replace('.5V6', '').replace(',6V0', ',0V0').replace('V6', 'V0')
    })

    // Draw bars
    const data = [
      { category: 'Achieve', value: achieveOffer.monthlyPayment, pattern: 'achieveStripes' },
      { category: 'Current', value: currentOffer.monthlyPayment, pattern: 'currentStripes' },
      { category: 'If you wait', value: waitOffer.monthlyPayment, pattern: 'waitStripes' },
    ]
    const xScale = scaleBand()
      .domain(data.map((d) => d.category))
      .range([
        graphWidth < 600 ? margin.left / 2 : margin.left,
        graphWidth < 600 ? width + margin.left / 2 : width - margin.right,
      ])
      .padding(0.1)
    const yScale = scaleLinear()
      .domain([0, max(data, (d) => d.value)])
      .nice()
      .range([height - margin.bottom, margin.top])

    const highestPoints = data.map((d) => {
      const x = xScale(d.category) + xScale.bandwidth() / 2
      const y = yScale(d.value)
      return { category: d.category, x, y }
    })

    svg
      .append('defs')
      .append('pattern')
      .attr('id', 'waitStripes')
      .attr('patternUnits', 'userSpaceOnUse')
      .attr('width', 6)
      .attr('height', 6)
      .attr('class', 'barLine1')
      .attr('x', Math.round(highestPoints?.[0]?.x))
      .attr('y', Math.round(highestPoints?.[0]?.y))
      .attr('patternTransform', 'rotate(45)')
      .call((pattern) => {
        pattern
          .append('rect')
          .attr('x', 0)
          .attr('y', 0)
          .attr('width', 4)
          .attr('height', 6)
          .attr('fill', '#CF264E')
        pattern
          .append('rect')
          .attr('x', 4)
          .attr('y', 0)
          .attr('width', 2)
          .attr('height', 6)
          .attr('fill', '#E55F80')
      })
    svg
      .append('defs')
      .append('pattern')
      .attr('id', 'achieveStripes')
      .attr('patternUnits', 'userSpaceOnUse')
      .attr('width', 6)
      .attr('height', 6)
      .attr('patternTransform', 'rotate(45)')
      .attr('class', 'barLine2')
      .attr('x', Math.round(highestPoints?.[1]?.x))
      .attr('y', Math.round(highestPoints?.[1]?.y))
      .call((pattern) => {
        pattern
          .append('rect')
          .attr('x', 0)
          .attr('y', 0)
          .attr('width', 4)
          .attr('height', 6)
          .attr('fill', '#3B6AF7')
        pattern
          .append('rect')
          .attr('x', 4)
          .attr('y', 0)
          .attr('width', 2)
          .attr('height', 6)
          .attr('fill', '#2C47F6')
      })

    svg
      .append('defs')
      .append('pattern')
      .attr('id', 'currentStripes')
      .attr('patternUnits', 'userSpaceOnUse')
      .attr('width', 6)
      .attr('height', 6)
      .attr('patternTransform', 'rotate(45)')
      .attr('class', 'barLine3')
      .attr('x', Math.round(highestPoints?.[2]?.x))
      .attr('y', Math.round(highestPoints?.[2]?.y))
      .call((pattern) => {
        pattern
          .append('rect')
          .attr('x', 0)
          .attr('y', 0)
          .attr('width', 4)
          .attr('height', 6)
          .attr('fill', '#C0CBD8')
        pattern
          .append('rect')
          .attr('x', 4)
          .attr('y', 0)
          .attr('width', 2)
          .attr('height', 6)
          .attr('fill', '#E7ECF3')
      })
    svg
      .selectAll('.bar')
      .data(data)
      .enter()
      .append('rect')
      .attr('class', 'bar')
      .attr('x', (d) => xScale(d.category))
      .attr('y', (d) => yScale(d.value) + 50)
      .attr('fill', (d) => `url(#${d.pattern})`)
      .attr('width', graphWidth < 713 ? 32 : 64)
      .attr('height', (d) => height - margin.bottom - yScale(d.value) - 50)

    // add Tooltips
    if (showAchieveTooltip) {
      addTooltip({
        title: tooltipTitleAchieve,
        titleIcon: true,
        value: `${numberFormatCurrency(achieveOffer.monthlyPayment)}/mo`,
        xAdd: -40,
        yAdd: -5,
        tooltipRef: tooltipRefAchieve,
        lineClassname: 'barLine1',
        showTooltip: showAchieveTooltip,
        titleStyle: 'border-0 bg-[#3300ff] text-white',
      })
    }
    if (showCurrentTooltip) {
      addTooltip({
        title: tooltipTitleCurrent,
        value: `${numberFormatCurrency(currentOffer.monthlyPayment)}/mo`,
        xAdd: -40,
        yAdd: -5,
        tooltipRef: tooltipRefCurrent,
        lineClassname: 'barLine2',
        showTooltip: showCurrentTooltip,
        titleStyle: 'bg-[#e7ecf3] text-[#5c708a]',
      })
    }
    if (showWaitTooltip) {
      addTooltip({
        title: tooltipTitleWait,
        value: `${numberFormatCurrency(waitOffer.monthlyPayment)}/mo`,
        xAdd: graphWidth < 460 ? -20 : -40,
        yAdd: -5,
        tooltipRef: tooltipRefWait,
        lineClassname: 'barLine3',
        showTooltip: showWaitTooltip,
        titleStyle: 'bg-[#fff5f8] text-[#cf264e]',
      })
    }
  }

  useEffect(() => {
    if (graphWidth > 0 && data) createGraph()
  }, [graphWidth, graphHeight, data])

  useLayoutEffect(() => {
    const setContainerWidth = () => {
      if (refChartSvg.current !== null) {
        setGraphWidth(refContainer?.current?.clientWidth)
      }
    }

    if (graphWidth === 0) setContainerWidth()
    window.addEventListener('resize', setContainerWidth)

    return () => {
      window.removeEventListener('resize', setContainerWidth)
    }
  }, [])

  return (
    <Box className="min-w-[328px] max-w-[1024px] relative" ref={refContainer}>
      <svg className="w-full max-w-full" height={graphHeight} id="barchart" ref={refChartSvg} />
      {showAchieveTooltip && (
        <div
          className="absolute min-w-[94px] text-center bg-white font-['Ultramarine'] shadow-[0px_15px_40px_-5px_rgba(82,82,82,0.26)] font-medium border-0 rounded-[5px] pointer-events-none
          before-tooltip"
          ref={tooltipRefAchieve}
          data-testid="tooltip-Achieve"
        />
      )}
      {showCurrentTooltip && (
        <div
          className="absolute min-w-[94px] text-center bg-white font-['Ultramarine'] shadow-[0px_15px_40px_-5px_rgba(82,82,82,0.26)] font-medium border-0 rounded-[5px] pointer-events-none
          before-tooltip"
          ref={tooltipRefCurrent}
          data-testid="tooltip-Current"
        />
      )}
      {showWaitTooltip && (
        <div
          className="absolute min-w-[94px] text-center bg-white font-['Ultramarine'] shadow-[0px_15px_40px_-5px_rgba(82,82,82,0.26)] font-medium border-0 rounded-[5px] pointer-events-none
          before-tooltip"
          ref={tooltipRefWait}
          data-testid="tooltip-Wait"
        />
      )}

      <style
        // this is added because is the only way to add the :after style to the tooltip
        dangerouslySetInnerHTML={{
          __html: `
      .before-tooltip:after {
        content: "";
            position: absolute;
            border-width: 9px;
            border-style: solid;
            margin-left: 8px;
            top: 90%;
            left: 35%;
            border-color: rgb(255, 255, 255) rgb(255, 255, 255) transparent transparent;
            transform: rotate(-90deg);
        }
            `,
        }}
      ></style>
    </Box>
  )
}
